import XYZ from 'ol/source/XYZ';

/**
 * Map tile provider configuration.
 *
 * Two providers:
 *  - local: self-hosted offline basemap served by the osm-tiles service
 *    through the fe-web nginx "/tiles/" proxy (see osm-tiles/README.md).
 *    No internet, no satellite layer.
 *  - vworld: online VWorld WMTS (road and satellite). VWorld serves tiles in
 *    {z}/{y}/{x} order while XYZ/OSM use {z}/{x}/{y}; the order is baked into
 *    each URL template so callers never deal with it.
 *
 * Posture comes from the deploy default in window.__MAP_CONFIG__.provider (the
 * optional /data/map-config.js), changeable without a rebuild:
 *  - "vworld" = online posture: probe VWorld and local at boot. The active
 *    provider is the user preference when both work, else whichever works. The
 *    user toggle (localStorage "map-provider") switches between them and is
 *    offered only when both are reachable.
 *  - "local" (default) = offline posture: probe only the same-origin local
 *    tiles, never reach out, no toggle.
 * When neither provider is reachable the map is marked unavailable so the UI can
 * show a placeholder instead of broken tiles. ensureMapProviderResolved() runs
 * the probe once at boot without blocking the app; map components wait on it
 * (via useMapProviderReady) before mounting, so a slow probe never delays login
 * or other pages. setProviderPreference() persists the toggle and reloads.
 */

export type ProviderName = 'local' | 'vworld';

interface MapProvider {
  /** Base (road) tile URL template. */
  base: string;
  /** Satellite tile URL template; null = provider has no satellite layer. */
  satellite: string | null;
  /** Max zoom the tiles exist at; OL overzooms (scales) beyond this. */
  maxZoom: number;
  attributions?: string;
}

const VWORLD_KEY = '5363C20D-EDEA-3436-88BC-B45CC374A9B4';

const PROVIDERS: Record<ProviderName, MapProvider> = {
  local: {
    base: '/tiles/{z}/{x}/{y}.png',
    satellite: null,
    maxZoom: 19,
    attributions: '© OpenMapTiles © OpenStreetMap contributors',
  },
  vworld: {
    base: `https://api.vworld.kr/req/wmts/1.0.0/${VWORLD_KEY}/Base/{z}/{y}/{x}.png`,
    satellite: `https://api.vworld.kr/req/wmts/1.0.0/${VWORLD_KEY}/Satellite/{z}/{y}/{x}.jpeg`,
    maxZoom: 19,
  },
};

const PROVIDER_PREF_KEY = 'map-provider';

declare global {
  interface Window {
    __MAP_CONFIG__?: { provider?: string };
  }
}

// "auto" is accepted for backward compatibility and is treated as online
// posture (vworld), which already self-heals to local when VWorld is down.
function normalizeProvider(value: string | null | undefined): ProviderName {
  return value === 'vworld' || value === 'auto' ? 'vworld' : 'local';
}

// Posture comes only from map-config.js: "vworld" = online-capable, anything
// else = offline. The user toggle is a separate per-browser preference.
function readDeployPosture(): ProviderName {
  return normalizeProvider(window.__MAP_CONFIG__?.provider);
}

function readProviderPreference(): ProviderName | null {
  try {
    const stored = localStorage.getItem(PROVIDER_PREF_KEY);
    if (stored === 'local' || stored === 'vworld') return stored;
  } catch {
    // private mode / storage disabled
  }
  return null;
}

let activeName: ProviderName = 'local';
let mapAvailable = true;
let toggleAvailable = false;

const PROBE_TIMEOUT_MS = 1500;
// A low-zoom VWorld Base tile over Korea, present at every VWorld deployment.
const VWORLD_PROBE_URL = `https://api.vworld.kr/req/wmts/1.0.0/${VWORLD_KEY}/Base/6/25/54.png`;
// A core Seoul tile that always exists in the offline korea.mbtiles. Used only
// to confirm the osm-tiles service is actually serving before falling back.
const LOCAL_PROBE_URL = '/tiles/14/13970/6344.png';

// Resolves true when the tile loads as an image, false on error or timeout.
function probeTile(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const settle = (ok: boolean) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    const timer = window.setTimeout(() => settle(false), PROBE_TIMEOUT_MS);
    img.onload = () => {
      window.clearTimeout(timer);
      settle(true);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      settle(false);
    };
    // Cache-buster so a cached tile cannot mask current reachability.
    img.src = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
  });
}

let resolved = false;
let resolving: Promise<void> | null = null;

async function resolveActiveProvider(): Promise<void> {
  if (readDeployPosture() !== 'vworld') {
    // Offline posture: probe only the same-origin local tiles, never reach out.
    const localOk = await probeTile(LOCAL_PROBE_URL);
    activeName = 'local';
    mapAvailable = localOk;
    toggleAvailable = false;
  } else {
    // Online posture: probe both so the toggle reflects real availability and a
    // missing osm-tiles service never produces a dead local basemap.
    const [vworldOk, localOk] = await Promise.all([
      probeTile(VWORLD_PROBE_URL),
      probeTile(LOCAL_PROBE_URL),
    ]);
    const pref = readProviderPreference() ?? 'vworld';
    activeName = pref === 'vworld' ? (vworldOk ? 'vworld' : 'local') : localOk ? 'local' : 'vworld';
    mapAvailable = vworldOk || localOk;
    toggleAvailable = vworldOk && localOk;
  }
  resolved = true;
}

/**
 * Kick off the boot probe that resolves the active provider, once. Idempotent:
 * every call returns the same promise. Start it at app boot so it is ready by
 * the time a map mounts; rendering never waits on it (map components gate their
 * own mount on this via useMapProviderReady), so a slow probe cannot delay the
 * rest of the app. Never rejects (probeTile resolves false on error/timeout).
 */
export function ensureMapProviderResolved(): Promise<void> {
  if (!resolving) resolving = resolveActiveProvider();
  return resolving;
}

/** True once the boot probe has settled. */
export const isMapProviderResolved = (): boolean => resolved;

export const getActiveProvider = (): ProviderName => activeName;

/** False only when neither provider is reachable (show a placeholder). */
export const isMapAvailable = (): boolean => mapAvailable;

/** True only when both providers are reachable, so a switch is meaningful. */
export const canToggleProvider = (): boolean => toggleAvailable;

/** Whether the given (default: active) provider has a satellite layer. */
export const providerHasSatellite = (name: ProviderName = activeName): boolean =>
  PROVIDERS[name].satellite !== null;

/** Persist the chosen provider and reload so the whole app rebuilds against it. */
export function setProviderPreference(name: ProviderName): void {
  try {
    localStorage.setItem(PROVIDER_PREF_KEY, name);
  } catch {
    // private mode / storage disabled
  }
  window.location.reload();
}

/** Base (road) tile source for the given (default: active) provider. */
export const createBaseTileSource = (name: ProviderName = activeName) =>
  new XYZ({
    url: PROVIDERS[name].base,
    maxZoom: PROVIDERS[name].maxZoom,
    attributions: PROVIDERS[name].attributions,
  });

/** Satellite tile source, or null when the provider has none. */
export const createSatelliteTileSource = (name: ProviderName = activeName) => {
  const url = PROVIDERS[name].satellite;
  return url
    ? new XYZ({
        url,
        maxZoom: PROVIDERS[name].maxZoom,
        attributions: PROVIDERS[name].attributions,
      })
    : null;
};
